/**
 * React Query hooks for the core and the register, one per named endpoint.
 * Query keys are the endpoint names, so invalidation can be expressed in
 * contract terms. Feature domains (deal models, billing, legal, diligence)
 * keep their hooks in api/<domain>.ts and reuse `keys` for cross-invalidation.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, data, setCsrfToken } from './http'
import type {
  CalculationCardQuery, CardsPage, CurrentUser, DealModelList, DealModelMode, DealModelModeState, Entitlements, GeoSuggestion, MarkersEnvelope,
  PersonalSettings, PropertyBundle, PropertyMarkerQuery, SavedArea, SavedList, SourceCatalogueEntry, UiConfig, UserLabel, UserPreferences, UserPreferencesPatch,
} from './types'

export const keys = {
  me: ['me'] as const,
  entitlements: ['entitlements'] as const,
  mode: ['dealModelMode'] as const,
  preferences: ['preferences'] as const,
  uiConfig: ['uiConfig'] as const,
  lists: ['lists'] as const,
  labels: ['labels'] as const,
  sources: ['sourceCatalogue'] as const,
  dealModels: ['dealModels'] as const,
  areas: ['geoAreas'] as const,
  suggest: (q: string) => ['geoSuggest', q] as const,
  cards: (query: CalculationCardQuery) => ['calculationCards', query] as const,
  markers: (query: PropertyMarkerQuery) => ['propertyMarkers', query] as const,
  property: (id: string) => ['property', id] as const,
  personal: (id: string) => ['personalSettings', id] as const,
}

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async ({ signal }) => {
      const me = data<CurrentUser>(await api('me', { signal }))
      setCsrfToken(me.csrfToken)
      return me
    },
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function useEntitlements() {
  return useQuery({ queryKey: keys.entitlements, queryFn: async ({ signal }) => data<Entitlements>(await api('entitlements', { signal })), staleTime: 5 * 60_000, retry: false })
}

/** Server-granted component access for the active (or any) experience. */
export function useAccess() {
  const entitlements = useEntitlements()
  const access = entitlements.data?.access ?? null
  return {
    ...entitlements,
    can: (component: string, experience: string | null = null) => Boolean(access?.experiences.some(grant => (experience === null || grant.key === experience) && grant.componentKeys.includes(component))),
    capability: (key: string) => entitlements.data?.capabilities.includes(key) ?? false,
  }
}

export function useDealModelMode() {
  return useQuery({ queryKey: keys.mode, queryFn: async ({ signal }) => data<DealModelModeState>(await api('dealModelMode', { signal })), retry: false })
}

export function usePutDealModelMode() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { expectedRevision: string; mode: DealModelMode }) => data<DealModelModeState>(await api('putDealModelMode', { body: input })),
    onSuccess: async (updated) => {
      client.setQueryData(keys.mode, updated)
      await Promise.all([
        client.invalidateQueries({ queryKey: ['calculationCards'], refetchType: 'none' }),
        client.invalidateQueries({ queryKey: ['propertyMarkers'], refetchType: 'none' }),
        client.invalidateQueries({ queryKey: keys.dealModels }),
      ])
    },
  })
}

export function usePreferences() {
  return useQuery({ queryKey: keys.preferences, queryFn: async ({ signal }) => data<UserPreferences>(await api('preferences', { signal })), staleTime: 60_000, retry: false })
}

export function usePatchPreferences() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (patch: UserPreferencesPatch) => data<UserPreferences>(await api('patchPreferences', { body: patch })),
    onSuccess: async (updated, patch) => {
      client.setQueryData(keys.preferences, updated)
      const affected: Promise<unknown>[] = []
      if (patch.dealModelIds !== undefined || patch.registerDefaults !== undefined || patch.defaultAssetClasses !== undefined || patch.refurbishmentDefault !== undefined) {
        affected.push(client.invalidateQueries({ queryKey: ['calculationCards'] }), client.invalidateQueries({ queryKey: ['propertyMarkers'] }))
      }
      if (patch.refurbishmentDefault !== undefined) affected.push(client.invalidateQueries({ queryKey: ['refurbishment'] }), client.invalidateQueries({ queryKey: ['dealsV2'] }))
      await Promise.all(affected)
    },
    onError: () => { void client.invalidateQueries({ queryKey: keys.preferences }) },
  })
}

export function useUiConfig() {
  return useQuery({ queryKey: keys.uiConfig, queryFn: async ({ signal }) => data<UiConfig>(await api('uiConfig', { signal })), staleTime: Infinity, retry: false })
}

export function useSavedLists() {
  return useQuery({ queryKey: keys.lists, queryFn: async ({ signal }) => data<SavedList[]>(await api('lists', { signal })), staleTime: 30_000 })
}

export function useListsForProperty(propertyId: string) {
  return useQuery({ queryKey: [...keys.lists, propertyId], queryFn: async ({ signal }) => data<SavedList[]>(await api('lists', { query: { propertyId }, signal })), staleTime: 10_000 })
}

export function useCreateList() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { name: string; description?: string }) => data<SavedList>(await api('createList', { body: input })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.lists }),
  })
}

export function useSetListMembership() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ listId, propertyId, member }: { listId: string; propertyId: string; member: boolean }) => {
      await api(member ? 'listMembership' : 'listMembershipRemove', { params: { listId, propertyId } })
      return { listId, propertyId, member }
    },
    onSettled: async (_result, _error, variables) => {
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.lists }),
        client.invalidateQueries({ queryKey: ['calculationCards'] }),
        client.invalidateQueries({ queryKey: keys.property(variables.propertyId) }),
      ])
    },
  })
}

export function useLabels() {
  return useQuery({ queryKey: keys.labels, queryFn: async ({ signal }) => data<UserLabel[]>(await api('labels', { signal })), staleTime: 60_000 })
}

export function useSourceCatalogue() {
  return useQuery({ queryKey: keys.sources, queryFn: async ({ signal }) => data<SourceCatalogueEntry[]>(await api('sourceCatalogue', { signal })), staleTime: 10 * 60_000, retry: false })
}

export function useDealModels(enabled = true) {
  return useQuery({ queryKey: keys.dealModels, queryFn: async ({ signal }) => data<DealModelList>(await api('dealModels', { signal })), enabled, staleTime: 60_000, retry: false })
}

export function useCards(query: CalculationCardQuery, enabled = true) {
  return useQuery({
    queryKey: keys.cards(query),
    queryFn: async ({ signal }) => data<CardsPage>(await api('calculationCards', { query: query as Record<string, string | number | boolean | undefined>, signal })),
    enabled,
    placeholderData: (previous) => previous,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function usePropertyMarkers(query: PropertyMarkerQuery, enabled = true) {
  return useQuery({
    queryKey: keys.markers(query),
    queryFn: async ({ signal }) => api<MarkersEnvelope>('propertyMarkers', { query: query as Record<string, string | number | boolean | undefined>, signal }),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: false,
  })
}

export function useGeoSuggest(q: string) {
  const trimmed = q.trim()
  return useQuery({
    queryKey: keys.suggest(trimmed),
    queryFn: async ({ signal }) => data<GeoSuggestion[]>(await api('geoSuggest', { query: { q: trimmed }, signal })),
    enabled: trimmed.length >= 2,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
    retry: false,
  })
}

export function useSavedAreas(enabled = true) {
  return useQuery({ queryKey: keys.areas, queryFn: async ({ signal }) => data<SavedArea[]>(await api('geoAreas', { signal })), enabled, staleTime: 60_000 })
}

export function useSaveArea() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: { name: string; polygon: [number, number][] }) => data<SavedArea>(await api('createGeoArea', { body: input })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.areas }),
  })
}

export function useDeleteArea() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (areaId: string) => { await api('deleteGeoArea', { params: { areaId } }); return areaId },
    onSuccess: () => client.invalidateQueries({ queryKey: keys.areas }),
  })
}

export function useProperty(propertyId: string) {
  return useQuery({ queryKey: keys.property(propertyId), queryFn: async ({ signal }) => data<PropertyBundle>(await api('property', { params: { propertyId }, signal })), enabled: propertyId.length > 0, retry: false })
}

export function usePropertyCard(propertyId: string) {
  return useQuery({
    queryKey: keys.cards({ propertyId, status: 'all', limit: 1 }),
    queryFn: async ({ signal }) => data<CardsPage>(await api('calculationCards', { query: { propertyId, status: 'all', limit: 1 }, signal })),
    enabled: propertyId.length > 0,
    retry: false,
  })
}

export function useSetVisited() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ propertyId, visited, expectedVisitRevision }: { propertyId: string; visited: boolean; expectedVisitRevision: number }) =>
      data<PersonalSettings>(await api('patchPersonalSettings', { params: { propertyId }, body: { visited, expectedVisitRevision } })),
    onSuccess: async (updated) => {
      client.setQueryData(keys.personal(updated.propertyId), updated)
      await Promise.all([
        client.invalidateQueries({ queryKey: ['calculationCards'] }),
        client.invalidateQueries({ queryKey: keys.property(updated.propertyId) }),
      ])
    },
  })
}
